import { z } from 'zod';
import { LANGUAGES, PERIODICITIES, PRODUCT_FAMILIES } from '../domain';

// Référentiels du tenant (BA-007, spec 03, spec 08 Paramètres).

const isoDate = /^\d{4}-\d{2}-\d{2}$/;
export const isoDateSchema = z
  .string()
  .regex(isoDate, 'Date attendue au format AAAA-MM-JJ')
  .refine(
    (v) =>
      !Number.isNaN(Date.parse(`${v}T00:00:00Z`)) &&
      new Date(`${v}T00:00:00Z`).toISOString().startsWith(v),
    'Date inexistante',
  );

export const timeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Heure attendue au format HH:MM');
export const codeSchema = z
  .string()
  .regex(/^[A-Z0-9_-]{1,20}$/, 'Code : 1 à 20 caractères A-Z, 0-9, _ ou -');
export const idParamSchema = z.object({ id: z.string().min(1).max(64) });

// --- Tenant ---

/** Options du tenant activables par l'administrateur (stockées dans `settings_json`). */
export const tenantOptionsSchema = z.object({
  /** Seconde opinion d'un modèle de langage sur les réponses libres (BA-060), texte anonymisé. */
  llmClassification: z.boolean(),
  /** Rapport mensuel de recouvrement envoyé par email aux administrateurs (BA-056). */
  monthlyReportEmail: z.boolean(),
  /** Numéros étrangers acceptés à l'import. */
  allowInternationalPhones: z.boolean(),
  /** Escalades confiées à l'agent du contrat plutôt qu'à l'équipe (BA-092). */
  assignEscalationsToAgents: z.boolean(),
});
export type TenantOptions = z.infer<typeof tenantOptionsSchema>;

const noticeDaysSchema = z.number().int().min(1).max(365);
/** Préavis et documents du cabinet (BA-079, BA-081), stockés dans `settings_json`. */
export const tenantNoticesSchema = z.object({
  /** Jours avant chaque échéance de prime pour les étapes ancrées au préavis. */
  dueNoticeDays: noticeDaysSchema,
  /** Rappel de fin de contrat, jours avant le terme ; nul : pas de rappel. */
  contractEndNoticeDays: noticeDaysSchema.nullable(),
  /** Attestation de paiement envoyée à l'assuré dès que la période en cours est réglée. */
  attestationAfterPayment: z.boolean(),
});
export type TenantNotices = z.infer<typeof tenantNoticesSchema>;
/** Surcharge par branche : nul reprend le réglage du cabinet. */
export const productLineNoticesSchema = z.object({
  dueNoticeDays: noticeDaysSchema.nullable(),
  contractEndNoticeDays: noticeDaysSchema.nullable(),
  attestationAfterPayment: z.boolean().nullable(),
});

export const tenantSettingsSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  kind: z.enum(['insurer', 'broker']),
  status: z.enum(['onboarding', 'active', 'suspended', 'closed']),
  timezone: z.string(),
  localeDefault: z.enum(LANGUAGES),
  sendWindowStart: z.string(),
  sendWindowEnd: z.string(),
  /** Bit 0 = lundi ... bit 6 = dimanche. */
  sendDays: z.number().int(),
  dailySendCap: z.number().int().nullable(),
  draftMode: z.boolean(),
  options: tenantOptionsSchema,
  notices: tenantNoticesSchema,
});

export const ONBOARDING_STEPS = [
  'insurer',
  'products',
  'users',
  'portfolio',
  'scenario',
  'templates',
  'messaging',
  'live',
] as const;

/** Liste de démarrage du tenant (BA-063) : chaque étape est déduite des données. */
export const onboardingSchema = z.object({
  steps: z.array(z.object({ key: z.enum(ONBOARDING_STEPS), done: z.boolean() })),
  completed: z.number().int(),
  total: z.number().int(),
  /** Tenant de démonstration : envois réels et paiements désactivés. */
  sandbox: z.boolean(),
});

export const tenantPatchSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    timezone: z.string().min(1).max(64),
    localeDefault: z.enum(LANGUAGES),
    sendWindowStart: timeOfDaySchema,
    sendWindowEnd: timeOfDaySchema,
    sendDays: z.number().int().min(1).max(127),
    dailySendCap: z.number().int().min(1).max(1_000_000).nullable(),
    options: tenantOptionsSchema.partial().strict(),
    notices: tenantNoticesSchema.partial().strict(),
  })
  .partial()
  .strict();

// --- Jours fériés ---

export const holidaySchema = z.object({
  id: z.string(),
  date: z.string(),
  label: z.string(),
  kind: z.enum(['fixed', 'estimated', 'confirmed']),
  scope: z.enum(['national', 'tenant']),
});
export const holidayListSchema = z.object({ items: z.array(holidaySchema) });
export const holidayQuerySchema = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
});
export const holidayCreateSchema = z.object({
  date: isoDateSchema,
  label: z.string().trim().min(1).max(120),
});

// --- Assureurs ---

const bankDetailsSchema = z.record(z.string().max(40), z.string().max(200));

export const insurerSchema = z.object({
  id: z.string(),
  code: z.string(),
  legalName: z.string(),
  displayName: z.string(),
  senderName: z.string().nullable(),
  legalMentions: z.string().nullable(),
  bankDetails: bankDetailsSchema.nullable(),
});
export type InsurerDto = z.infer<typeof insurerSchema>;
export const insurerListSchema = z.object({ items: z.array(insurerSchema) });

export const insurerCreateSchema = z.object({
  code: codeSchema,
  legalName: z.string().trim().min(1).max(200),
  displayName: z.string().trim().min(1).max(120),
  /** Identifiant d'expéditeur SMS : 11 caractères alphanumériques au plus. */
  senderName: z
    .string()
    .regex(/^[A-Za-z0-9 ]{1,11}$/)
    .nullable()
    .optional(),
  legalMentions: z.string().max(1000).nullable().optional(),
  bankDetails: bankDetailsSchema.nullable().optional(),
});
/** Le code est immuable : il entre dans la numérotation des reçus. */
export const insurerPatchSchema = insurerCreateSchema.omit({ code: true }).partial().strict();

// --- Lignes de produit ---

export const productLineSchema = z.object({
  id: z.string(),
  insurerId: z.string(),
  code: z.string(),
  labelFr: z.string(),
  labelAr: z.string().nullable(),
  family: z.enum(PRODUCT_FAMILIES),
  defaultPeriodicity: z.enum(PERIODICITIES),
  graceDays: z.number().int(),
  suspensionDays: z.number().int().nullable(),
  defaultScenarioId: z.string().nullable(),
  notices: productLineNoticesSchema,
});
export const productLineListSchema = z.object({ items: z.array(productLineSchema) });
export const productLineQuerySchema = z.object({ insurerId: z.string().optional() });

export const productLineCreateSchema = z.object({
  insurerId: z.string().min(1),
  code: codeSchema,
  labelFr: z.string().trim().min(1).max(120),
  labelAr: z.string().trim().max(120).nullable().optional(),
  family: z.enum(PRODUCT_FAMILIES),
  defaultPeriodicity: z.enum(PERIODICITIES).optional(),
  graceDays: z.number().int().min(0).max(365).optional(),
  /** Nul : pas de suspension (jamais activée par défaut, spec 11). */
  suspensionDays: z.number().int().min(1).max(365).nullable().optional(),
  defaultScenarioId: z.string().nullable().optional(),
  notices: productLineNoticesSchema.partial().strict().optional(),
});
export const productLinePatchSchema = productLineCreateSchema
  .omit({ insurerId: true, code: true })
  .partial()
  .strict();

// --- Agents ---

export const agentSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  branch: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  userId: z.string().nullable(),
});
export const agentListSchema = z.object({ items: z.array(agentSchema) });

export const agentCreateSchema = z.object({
  code: codeSchema,
  name: z.string().trim().min(1).max(120),
  branch: z.string().trim().max(120).nullable().optional(),
  phone: z.string().max(40).nullable().optional(),
  email: z.string().max(254).nullable().optional(),
  userId: z.string().nullable().optional(),
});
export const agentPatchSchema = agentCreateSchema.omit({ code: true }).partial().strict();
